import { CheckIcon, BoltIcon } from '@heroicons/react/24/outline';
import { cn } from '@/utils/cn';

export interface Engine {
  id: string;
  name: string;
  vendor: string;
  description: string;
  tags: string[];
  latency: 'Fast' | 'Balanced' | 'Slow';
  quality: number;
  recommended?: boolean;
  disabled?: boolean;
  disabledReason?: string;
}

interface EnginePickerProps {
  engines: Engine[];
  value: string | null;
  onChange: (id: string) => void;
}

export function EnginePicker({ engines, value, onChange }: EnginePickerProps) {
  return (
    <div className="flex gap-3 overflow-x-auto pb-1">
      {engines.map((engine) => {
        const active = value === engine.id;
        return (
          <button
            key={engine.id}
            type="button"
            disabled={engine.disabled}
            onClick={() => onChange(engine.id)}
            title={engine.disabled && engine.disabledReason ? engine.disabledReason : engine.description}
            className={cn(
              'group relative min-w-[200px] flex-1 shrink-0 overflow-hidden rounded-lg border p-3 text-left transition-all',
              'border-gray-200 bg-white hover:border-indigo-300 hover:shadow-sm dark:border-gray-700 dark:bg-gray-800 dark:hover:border-indigo-500',
              active && 'border-indigo-500 bg-indigo-50 ring-1 ring-indigo-500 dark:border-indigo-500 dark:bg-indigo-900/20',
              engine.disabled && 'opacity-60 cursor-not-allowed'
            )}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="flex items-center gap-1.5">
                  <h4 className="truncate text-sm font-semibold text-gray-900 dark:text-white">{engine.name}</h4>
                  {engine.recommended && (
                    <BoltIcon className="h-3.5 w-3.5 shrink-0 text-amber-500" aria-label="Suggested" />
                  )}
                </div>
                <p className="truncate text-xs text-gray-500 dark:text-gray-400">{engine.vendor}</p>
              </div>
              <span
                className={cn(
                  'flex h-5 w-5 shrink-0 items-center justify-center rounded-full border transition-colors',
                  active ? 'border-indigo-600 bg-indigo-600 text-white dark:border-indigo-500 dark:bg-indigo-500' : 'border-gray-300 dark:border-gray-600'
                )}
              >
                {active && <CheckIcon className="h-3.5 w-3.5" />}
              </span>
            </div>

            <div className="mt-2 flex items-center justify-between text-[11px] text-gray-500 dark:text-gray-400">
              <span className="inline-flex items-center gap-1">
                <BoltIcon className="h-3 w-3" /> {engine.latency}
              </span>
              <span className="flex gap-0.5">
                {Array.from({ length: 5 }).map((_, i) => (
                  <span
                    key={i}
                    className={cn(
                      'h-1.5 w-2.5 rounded-full',
                      i < engine.quality ? 'bg-indigo-500' : 'bg-gray-200 dark:bg-gray-600'
                    )}
                  />
                ))}
              </span>
            </div>
          </button>
        );
      })}
    </div>
  );
}
